import { ChangeEvent, FC, useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  RadioButtonUncheckedOutlined as RadioIcon,
  RadioButtonCheckedOutlined as CheckedRadioIcon,
} from '@mui/icons-material';
import { menuItemStyles } from '@utils/styles';
import CustomTextField from '../../../../components/CustomTextField/CustomTextField';
import { useDispatch } from 'react-redux';
import { setToastMessage } from '@stores/App/slice';
import {
  APP_STORE_APP_ID_REGEX,
  APP_STORE_LINK_REGEX,
  APP_STORE_LINK_REGEX_2,
  APPLICATION_STORES,
  DataAccessProvider,
  EMAIL_REGEX,
  PLAY_STORE_APP_ID_REGEX,
  PLAY_STORE_LINK_REGEX,
  TOAST_TYPES,
} from '@constants/constants';
import {
  GameDetailsPayload,
  useGameDetailsMutation,
  useLazyGetCategoriesAndGenresQuery,
  useLazyGetGameByIdQuery,
  useUpdateGameDetailsMutation,
} from '@api/games';
import { SelectWithLabel } from '@components';
import { useSelector } from 'react-redux';
import { RootState } from '@stores/store';
import useCompany from '@hooks/api/useCompany';
import { useLazyFetchApplicationDetailsQuery } from '@api/register';
import { processRatingString } from '@utils/common';
import GameDetailsCard, {
  IGameDetails,
} from '../../GettingStartedDashboards/components/GameData/GameDetails';
import {
  createFormattedPercentValue,
  handlePercentageChange,
  stripPercentSymbol,
} from '@utils/percentageInputsUtils';
import DealSetupFooter from './components/DealSetupFooter';

interface FormFieldI {
  id: string;
  label: string;
  key: string;
  required?: boolean;
}

const dealFields = [
  {
    id: 'deal-name',
    label: 'Deal Name',
    key: 'dealName',
    required: true,
  },
];

const gameFields = [
  {
    id: 'appstore-link',
    label: 'Appstore Link',
    key: 'appStoreLink',
  },
  {
    id: 'playstore-link',
    label: 'Playstore Link',
    key: 'playStoreLink',
  },
];

const signingAuthorityFormFields = [
  {
    id: 'signing-authority-name',
    label: 'Signing Authority Name',
    key: 'signingAuthority',
    required: false,
  },
  {
    id: 'signing-authority-email',
    label: 'Signing Authority Email',
    key: 'signingAuthorityEmail',
    required: false,
  },
  {
    id: 'signing-authority-designation',
    label: 'Signing Authority Designation',
    key: 'signingAuthorityTitle',
    required: false,
  },
];

const statusOptions = [
  { label: 'Funded', value: 'FUNDED' },
  { label: 'Not Funded', value: 'NOT_FUNDED' },
  { label: 'Self Served', value: 'SELF_SERVED' },
];

const selectStyles = {
  '.MuiSvgIcon-root ': {
    fill: 'rgba(0, 0, 0, 0.54) !important',
  },
  '& fieldset': {
    borderColor: '#737F86',
  },
  borderRadius: '4px',
  paddingRight: '0.5rem',
};

interface GameDetailsProps {
  gameId: string;
  setGameId: React.Dispatch<React.SetStateAction<string>>;
  handleCancelClick: () => void;
  dealUpdatedDetailsComponent: JSX.Element;
}

type GameDetailsRow = Record<string, string>;

const initialGameDetails: GameDetailsRow = {
  dealName: '',
  name: '',
  companyName: '',
  genre: '',
  category: '',
  status: 'NOT_FUNDED',
  signingAuthority: '',
  signingAuthorityEmail: '',
  signingAuthorityTitle: '',
  address: '',
  appStoreLink: '',
  playStoreLink: '',
  clientCode: '',
  targetIrr: '',
};

const initialErrors = Object.keys(initialGameDetails).reduce(
  (acc, key) => {
    acc[key] = false;
    return acc;
  },
  {} as Record<string, boolean>,
);

const initialTouched = Object.keys(initialGameDetails).reduce(
  (acc, key) => {
    acc[key] = false;
    return acc;
  },
  {} as Record<string, boolean>,
);

const requiredKeys = ['dealName', 'name', 'clientCode'];

const GameDetails: FC<GameDetailsProps> = ({
  gameId,
  setGameId,
  handleCancelClick,
  dealUpdatedDetailsComponent,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();

  const companies = useSelector((state: RootState) => state?.CompanyListData);

  const [gameDetails, setGameDetails] = useState<GameDetailsRow>(initialGameDetails);
  const [monthlyRefresh, setMonthlyRefresh] = useState(true);
  const [categoryOptions, setCategoryOptions] = useState<{ name: string; genres: string[] }[]>([]);
  const [genreOptions, setGenreOptions] = useState<string[]>([]);
  const [categoriesFetched, setCategoriesFetched] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [error, setErrors] = useState<Record<string, boolean | string>>(initialErrors);
  const [loader, setLoader] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>(initialTouched);
  const [companiesFetched, setCompaniesFetched] = useState<boolean>(false);
  const [playStoreApp, setPlayStoreApp] = useState<null | IGameDetails>(null);
  const [appStoreApp, setAppStoreApp] = useState<null | IGameDetails>(null);

  const [fetchCategories] = useLazyGetCategoriesAndGenresQuery();
  const [fetchGameById] = useLazyGetGameByIdQuery();
  const [createGameDetails] = useGameDetailsMutation();
  const [updateGameDetails] = useUpdateGameDetailsMutation();
  const { getCompanyList } = useCompany();
  const [fetchApplicationDetailsApi] = useLazyFetchApplicationDetailsQuery();

  useEffect(() => {
    fetchCategoryOptions();
    if (!companies?.companyList?.length && !companiesFetched) {
      getCompanyList();
      setCompaniesFetched(true);
    }
  }, []);

  useEffect(() => {
    if (gameId && categoriesFetched) {
      fetchExistingGameDetails(gameId);
    }
  }, [categoriesFetched]);

  const fetchCategoryOptions = async () => {
    try {
      const { data } = await fetchCategories().unwrap();
      setCategoryOptions(data.categories);
    } catch (error: any) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: error?.error?.message,
        }),
      );
    } finally {
      setCategoriesFetched(true);
    }
  };

  const fetchExistingGameDetails = async (id: string) => {
    try {
      const { data } = await fetchGameById(id).unwrap();
      const selectedCategory = categoryOptions.find((cat) => cat.name === data.category);
      setGenreOptions(selectedCategory?.genres || []);
      let metadata: Record<string, IGameDetails> = {};
      try {
        metadata = JSON.parse(data.metadata);

        if (metadata['APP_STORE']) {
          setAppStoreApp(metadata['APP_STORE']);
        }

        if (metadata['PLAY_STORE']) {
          setPlayStoreApp(metadata['PLAY_STORE']);
        }
      } catch {}

      const mappedData: GameDetailsRow = {
        dealName: data.dealName,
        companyName: data.companyName,
        clientCode: data.clientCode,
        name: data.name,
        genre: data.genre,
        category: data.category,
        signingAuthority: data.signingAuthority,
        signingAuthorityEmail: data.signingAuthorityEmail,
        signingAuthorityTitle: data.signingAuthorityTitle,
        status: data.status,
        address: data.address || '',
        appStoreLink: metadata?.['APP_STORE']?.appUrl || '',
        playStoreLink: metadata?.['PLAY_STORE']?.appUrl || '',
        targetIrr: data.targetIrr ? createFormattedPercentValue(data.targetIrr * 100) : '',
      };

      validateForm(mappedData);
      setMonthlyRefresh(!!data.refreshRequired);
      setGameDetails(mappedData);
    } catch (error: any) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: error?.error?.message,
        }),
      );
    }
  };

  const fetchApplicationDetails = async (
    appUrl: string,
    store: (typeof APPLICATION_STORES)[keyof typeof APPLICATION_STORES],
  ) => {
    try {
      const { data } = await fetchApplicationDetailsApi({
        store,
        appUrl,
      }).unwrap();
      if (!data) return;
      const appDetails = {
        marketPlace: data.marketPlace,
        appUrl: data.appUrl,
        name: data.name,
        icon: data.icon,
        developerName: data.developerName,
        rating: processRatingString(data.rating),
        reviews: data.reviews,
        downloads: data.downloads,
      };

      store === APPLICATION_STORES.PLAY_STORE
        ? setPlayStoreApp(appDetails)
        : setAppStoreApp(appDetails);
    } catch (error: any) {
      console.error(error);
      store === APPLICATION_STORES.PLAY_STORE ? setPlayStoreApp(null) : setAppStoreApp(null);
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: error?.error?.message || 'Unable to fetch application details.',
        }),
      );
      setErrors((prev) => ({
        ...prev,
        [store === APPLICATION_STORES.PLAY_STORE ? 'playStoreLink' : 'appStoreLink']:
          'Failed to fetch Application details',
      }));
    }
  };

  const handleTargetIrrChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    handlePercentageChange(e, (result) => {
      setGameDetails((prev) => ({
        ...prev,
        targetIrr: result,
      }));
      validateForm({ ...gameDetails, targetIrr: result });
    });
  };

  const handleFieldChange = (key: string, value: any) => {
    setTouched((prev) => ({ ...prev, [key]: true }));

    const newGameDetails = { ...gameDetails };

    if (typeof value === 'string') {
      newGameDetails[key] = value;
    }

    if (key === 'category') {
      const selectedCategory = categoryOptions.find((cat) => cat.name === value);
      setGenreOptions(selectedCategory?.genres || []);
      newGameDetails['genre'] = '';
    }

    if (key === 'clientCode') {
      if (typeof value === 'string') {
        newGameDetails['address'] =
          companies.companyList.find((company) => company.clientCode === value)?.address || '';
        newGameDetails['companyName'] =
          companies.companyList.find((company) => company.clientCode === value)?.name || '';
      } else {
        // change the company address field
        newGameDetails['address'] = value.address;
        newGameDetails['clientCode'] = value.clientCode;
        newGameDetails['companyName'] = value.name;
      }
    }

    if (key === 'status') {
      if (value === 'FUNDED' && !gameDetails.targetIrr) {
        newGameDetails['targetIrr'] = createFormattedPercentValue(25);
      }
    }

    if (key === 'playStoreLink') {
      if (PLAY_STORE_APP_ID_REGEX.test(value)) {
        value = `https://play.google.com/store/apps/details?id=${value}`;
        newGameDetails['playStoreLink'] = value;
      }
    }

    if (key === 'appStoreLink') {
      if (APP_STORE_APP_ID_REGEX.test(value) || APP_STORE_APP_ID_REGEX.test('id' + value)) {
        value = `https://apps.apple.com/app/id/${value.replace(/[a-zA-Z]/g, '')}`;
        newGameDetails['appStoreLink'] = value;
      }
    }

    setGameDetails(newGameDetails);
    validateForm(newGameDetails);

    if (
      key === 'appStoreLink' &&
      (APP_STORE_LINK_REGEX.test(value) || APP_STORE_LINK_REGEX_2.test(value))
    ) {
      fetchApplicationDetails(value, APPLICATION_STORES.APP_STORE);
    }
    if (key === 'playStoreLink' && PLAY_STORE_LINK_REGEX.test(value)) {
      fetchApplicationDetails(value, APPLICATION_STORES.PLAY_STORE);
    }

    if (key === 'appStoreLink' && value.length === 0) {
      setAppStoreApp(null);
    }
    if (key === 'playStoreLink' && value.length === 0) {
      setPlayStoreApp(null);
    }
  };

  const validateForm = (details: GameDetailsRow) => {
    const fieldErrors: Record<string, boolean | string> = {};
    for (const key of requiredKeys) {
      fieldErrors[key] = !details[key];
    }

    if (details.status === 'FUNDED' && !details.targetIrr) {
      fieldErrors.targetIrr = true;
    } else {
      fieldErrors.targetIrr = false;
    }

    if (details.signingAuthorityEmail && !EMAIL_REGEX.test(details.signingAuthorityEmail)) {
      fieldErrors.signingAuthorityEmail = true;
    } else {
      fieldErrors.signingAuthorityEmail = false;
    }

    if (
      details.appStoreLink &&
      !(
        APP_STORE_LINK_REGEX.test(details.appStoreLink) ||
        APP_STORE_LINK_REGEX_2.test(details.appStoreLink)
      )
    ) {
      fieldErrors.appStoreLink = 'Appstore Link not valid';
    } else {
      fieldErrors.appStoreLink = false;
    }

    if (details.playStoreLink && !PLAY_STORE_LINK_REGEX.test(details.playStoreLink)) {
      fieldErrors.playStoreLink = 'Playstore Link not valid';
    } else {
      fieldErrors.playStoreLink = false;
    }

    setErrors(fieldErrors);
    setIsFormValid(!Object.values(fieldErrors).some(Boolean));
  };

  function createAppDataJSON(appStore: null | IGameDetails, playStore: null | IGameDetails) {
    const result: Record<string, IGameDetails> = {};

    if (appStore) {
      result['APP_STORE'] = {
        marketPlace: appStore.marketPlace,
        appUrl: appStore.appUrl,
        name: appStore.name,
        icon: appStore.icon,
        developerName: appStore.developerName,
        rating: appStore.rating,
        reviews: appStore.reviews,
        downloads: appStore.downloads,
      };
    }

    if (playStore) {
      result['PLAY_STORE'] = {
        marketPlace: playStore.marketPlace,
        appUrl: playStore.appUrl,
        name: playStore.name,
        icon: playStore.icon,
        developerName: playStore.developerName,
        rating: playStore.rating,
        reviews: playStore.reviews,
        downloads: playStore.downloads,
      };
    }

    if (Object.keys(result).length === 0) {
      return null;
    }

    return JSON.stringify(result);
  }

  const handleSaveClick = async () => {
    try {
      setLoader(true);
      const payload: GameDetailsPayload = {
        name: gameDetails.name,
        dealName: gameDetails.dealName,
        companyName: gameDetails.companyName,
        signingAuthority: gameDetails.signingAuthority || null,
        signingAuthorityEmail: gameDetails.signingAuthorityEmail || null,
        signingAuthorityTitle: gameDetails.signingAuthorityTitle || null,
        address: gameDetails.address || null,
        genre: gameDetails.genre || null,
        category: gameDetails.category || null,
        refreshRequired: monthlyRefresh,
        clientCode: gameDetails.clientCode,
        status: gameDetails.status,
        dataSource: DataAccessProvider.NONE,
        metadata: createAppDataJSON(appStoreApp, playStoreApp),
        targetIrr: gameDetails.targetIrr ? stripPercentSymbol(gameDetails.targetIrr) / 100 : null,
      };

      if (gameId) {
        // Update Api Call
        await updateGameDetails({
          gameId,
          body: payload,
        }).unwrap();

        fetchExistingGameDetails(gameId);
      } else {
        // Create Api Call
        const result = await createGameDetails(payload).unwrap();

        dispatch(
          setToastMessage({
            type: TOAST_TYPES.SUCCESS,
            message: 'Game Details Updated successfully. User can goto Deal Details.',
          }),
        );

        if (!gameId) {
          setGameId(result?.data?.gameId);
          fetchExistingGameDetails(result?.data?.gameId);
        }
      }

      getCompanyList();
    } catch (error: any) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: error?.error?.message || 'Updating Game Details failed. Please try again.',
        }),
      );
    } finally {
      setLoader(false);
    }
  };

  const renderTextFields = (fields: FormFieldI[]) =>
    fields.map(({ id, label, key, required = false }) => (
      <CustomTextField
        key={id}
        id={id}
        required={required}
        label={label}
        customStyle={{ width: '100%' }}
        value={gameDetails[key] ?? ''}
        error={!!error[key] && touched[key]}
        helperText={typeof error[key] === 'string' ? (error[key] as string) : ''}
        onFieldChange={(e) => handleFieldChange(key, e.target.value)}
      />
    ));

  const renderSelectField = (
    label: string,
    key: string,
    options: (string | { label: string; value: string })[],
  ) => (
    <FormControl fullWidth>
      <InputLabel id={key}>{label}</InputLabel>
      <Select
        labelId={key}
        disabled={options.length === 0}
        value={gameDetails[key]}
        label={label}
        onChange={(e) => handleFieldChange(key, e.target.value)}
        sx={{
          height: '56px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#6C6C6C',
          },
        }}
      >
        {options.map((option) => (
          <MenuItem
            key={typeof option === 'string' ? option : option.value}
            value={typeof option === 'string' ? option : option.value}
            sx={menuItemStyles}
          >
            {typeof option === 'string' ? option : option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box
        id="game-details"
        sx={{
          flex: '1',
          p: '1rem 1rem 1rem 0',
          overflowY: 'auto',
        }}
      >
        {loader ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress
              style={{
                width: '4rem',
                height: '4rem',
                color: theme.palette.primary.main,
              }}
            />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                gap: '1rem',
                mb: '2rem',
              }}
            >
              <Box sx={{ gridColumn: '1 / -1' }}>{renderTextFields(dealFields)}</Box>

              <SelectWithLabel
                label="Company"
                required
                field="clientCode"
                labelId="company-select-label"
                selectStyles={selectStyles}
                menuItems={companies.companyList.map((company) => ({
                  label: company.name,
                  value: company.clientCode || '',
                }))}
                onChange={(value) => handleFieldChange('clientCode', value || '')}
                isAddNew={true}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 400,
                    },
                  },
                }}
                value={gameDetails.clientCode}
              />
              <CustomTextField
                id={'client-code'}
                required={true}
                disabled={true}
                label={'Client Code'}
                customStyle={{ width: '100%' }}
                value={gameDetails['clientCode']}
                error={!!error['clientCode'] && touched['clientCode']}
                onFieldChange={(e) => handleFieldChange('clientCode', e.target.value)}
              />
              <CustomTextField
                id={'company-address'}
                label={'Company Address'}
                customStyle={{ width: '100%' }}
                value={gameDetails['address']}
                error={!!error['address'] && touched['address']}
                onFieldChange={(e) => handleFieldChange('address', e.target.value)}
              />
              {renderSelectField('Status', 'status', statusOptions)}
            </Box>
            <Typography fontSize="1rem" fontWeight={700} sx={{ color: '#162936', mb: '1.25rem' }}>
              Game
            </Typography>

            {gameId && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: '1rem',
                  mb: '1rem',
                }}
              >
                <CustomTextField
                  id="game-id"
                  label="Game ID"
                  customStyle={{ width: '100%' }}
                  value={gameId}
                  disabled
                  onFieldChange={() => {}}
                />
              </Box>
            )}

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: '1rem',
                mb: '1rem',
              }}
            >
              <CustomTextField
                id="name"
                required
                label="Game Name"
                customStyle={{ width: '100%' }}
                value={gameDetails.name}
                error={!!error['name'] && touched['name']}
                onFieldChange={(e) => handleFieldChange('name', e.target.value)}
              />
              <CustomTextField
                id="target-irr"
                label="Target IRR"
                required={gameDetails.status === 'FUNDED'}
                customStyle={{ width: '100%' }}
                value={gameDetails.targetIrr}
                error={!!error['targetIrr'] && touched['targetIrr']}
                onFieldChange={(e) => handleTargetIrrChange(e)}
              />
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '1rem',
                }}
              >
                {renderSelectField(
                  'Category',
                  'category',
                  categoryOptions.map((cat) => cat.name),
                )}
                {renderSelectField('Genre', 'genre', genreOptions)}
              </Box>
            </Box>

            <Stack direction={'column'} gap={'1rem'} mb={'2rem'}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                  gap: '1rem',
                }}
              >
                {renderTextFields(gameFields)}
              </Box>

              {(appStoreApp || playStoreApp) && (
                <Stack
                  gap={isMobile ? 2 : 1}
                  direction={isMobile ? 'column' : 'row'}
                  flexWrap="wrap"
                >
                  {appStoreApp && (
                    <GameDetailsCard
                      showEditIcon={false}
                      setIsEditGameOpen={() => {}}
                      category={null}
                      genre={null}
                      gameDetails={appStoreApp}
                    />
                  )}

                  {playStoreApp && (
                    <GameDetailsCard
                      showEditIcon={false}
                      setIsEditGameOpen={() => {}}
                      category={null}
                      genre={null}
                      gameDetails={playStoreApp}
                    />
                  )}
                </Stack>
              )}
            </Stack>

            <Typography fontSize="1rem" fontWeight={700} sx={{ color: '#162936', mb: '1.25rem' }}>
              Signing Authority
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                gap: '1rem',
                mb: '2rem',
              }}
            >
              {renderTextFields(signingAuthorityFormFields)}
            </Box>

            <Typography fontSize="1rem" fontWeight={700} sx={{ color: '#162936' }}>
              Monthly Refresh
            </Typography>
            <Box sx={{ display: 'flex', gap: '1rem' }}>
              {['Yes', 'No'].map((label, i) => {
                const value = i === 0;
                return (
                  <FormControlLabel
                    key={label}
                    sx={{
                      width: 'fit-content',
                      borderRadius: '0.5rem',
                      pt: '0.5rem',
                      mx: 0,
                      '& .MuiFormControlLabel-label': {
                        fontFamily: 'Manrope, sans-serif',
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        color: '#162936',
                      },
                    }}
                    label={label}
                    control={
                      <Checkbox
                        checked={monthlyRefresh === value}
                        onChange={() => setMonthlyRefresh(value)}
                        icon={<RadioIcon />}
                        checkedIcon={<CheckedRadioIcon />}
                      />
                    }
                  />
                );
              })}
            </Box>
          </>
        )}
      </Box>

      <DealSetupFooter
        dealUpdatedDetailsComponent={dealUpdatedDetailsComponent}
        onCancel={handleCancelClick}
        onSave={handleSaveClick}
        isSubmitEnabled={isFormValid}
      />
    </Box>
  );
};

export default GameDetails;
